import { DAILY5_START_DATE } from '../config'
import { useLocation } from 'wouter'
import archivePuzzleExists from '../util/archivePuzzleExists'
import getDateForPuzzleNumber from '../util/getDateForPuzzleNumber'
import getLocalDate from '../util/getLocalDate'
import getPuzzleNumberForDate from '../util/getPuzzleNumberForDate'

type DailyPuzzlePage = {
	name: 'daily-puzzle'
	puzzleType: 'daily5'
	puzzleDate: string
	puzzleNumber: number
}

type ArchivePuzzlePage = {
	name: 'archive-puzzle'
	puzzleType: 'daily5-archive'
	puzzleDate: string
	puzzleNumber: number
}

type ArchiveListPage = {
	name: 'archive-list'
}

type Page = DailyPuzzlePage | ArchivePuzzlePage | ArchiveListPage

type PuzzlePage = DailyPuzzlePage | ArchivePuzzlePage

const defaultPage: DailyPuzzlePage = {
	name: 'daily-puzzle',
	puzzleDate: getLocalDate(),
	puzzleType: 'daily5',
	puzzleNumber: getPuzzleNumberForDate(getLocalDate(), DAILY5_START_DATE, 1),
}

export function isDailyPuzzlePage(page: Page): page is DailyPuzzlePage {
	return page.name === 'daily-puzzle'
}

export function isPuzzlePage(page: Page): page is PuzzlePage {
	return page.name === 'daily-puzzle' || page.name === 'archive-puzzle'
}

export function isArchivePuzzlePage(page: Page): page is ArchivePuzzlePage {
	return page.name === 'archive-puzzle'
}

export function isArchiveListPage(page: Page): page is ArchiveListPage {
	return page.name === 'archive-list'
}

const usePageRouter = (): Page => {
	const [location, navigate] = useLocation()
	const path = location.split('?')[0]

	// Should we show the default daily puzzle page?
	if (path === '/') {
		return defaultPage
	}

	// Should we show the archive list?
	if (path === '/archive') {
		return {
			name: 'archive-list',
		}
	}

	// Should we show an archive puzzle?
	if (path.startsWith('/archive/daily')) {
		const puzzleNumber = Number(path.split('/')[3])
		if (archivePuzzleExists(puzzleNumber)) {
			return {
				name: 'archive-puzzle',
				puzzleDate: getDateForPuzzleNumber(puzzleNumber, DAILY5_START_DATE),
				puzzleType: 'daily5-archive',
				puzzleNumber,
			}
		} else {
			// The requested archive puzzle is unavailable, replace url with
			// archive list page and return that page's config
			navigate('/archive', { replace: true })
			return {
				name: 'archive-list',
			}
		}
	}

	// Encountered unknown page, replace url with root page and return default
	// page configuration
	navigate('/', { replace: true })
	return defaultPage
}

export default usePageRouter
